import React, { useState, useRef } from 'react';
import { PhotoIcon, ArrowUpTrayIcon, LinkIcon, SparklesIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { getAdminAccessToken } from '../../../utils/authToken';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const ImageBlock = ({ block, isActive, onChange }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [showAiPrompt, setShowAiPrompt] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [aiPrompt, setAiPrompt] = useState('');
  const fileInputRef = useRef(null);
  
  const { url = '', alt = '', caption = '' } = block.content || {};

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be less than 5MB');
      return;
    }

    setIsUploading(true);

    try {
      // Create FormData
      const formData = new FormData();
      formData.append('image', file);

      // Get auth token
      const token = getAdminAccessToken();

      // Upload to server
      const response = await axios.post(
        `${API_BASE_URL}/api/assets/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.url) {
        // Convert relative URL to full URL for display
        const imageUrl = response.data.url.startsWith('/') 
          ? `${API_BASE_URL}${response.data.url}` 
          : response.data.url;
        onChange({ url: imageUrl, alt: file.name });
      }
    } catch (error) {
      console.error('[ImageBlock Upload] Upload failed:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleUrlSubmit = () => {
    if (urlInput.trim()) {
      onChange({ url: urlInput.trim() });
      setShowUrlInput(false);
      setUrlInput('');
    }
  };

  const handleAiGenerate = async () => {
    if (!aiPrompt.trim()) {
      alert('Please enter a description for the image');
      return;
    }

    setIsGenerating(true);

    try {
      const token = getAdminAccessToken();
      const response = await axios.post(
        `${API_BASE_URL}/api/assets/blog/generate-image`,
        {
          prompt: aiPrompt,
          aspectRatio: '16:9'
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.url) {
        // Convert relative URL to full URL for display
        const imageUrl = response.data.url.startsWith('/') 
          ? `${API_BASE_URL}${response.data.url}` 
          : response.data.url;
        onChange({ url: imageUrl, alt: aiPrompt });
        setShowAiPrompt(false);
        setAiPrompt('');
      }
    } catch (error) {
      console.error('AI generation failed:', error);
      alert('Failed to generate image. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  if (!url) {
    return (
      <div className="image-block border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
        <PhotoIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
        <p className="text-gray-500 mb-4">Add an image</p>
        
        <div className="flex flex-col sm:flex-row gap-3 justify-center flex-wrap">
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading || isGenerating}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            <ArrowUpTrayIcon className="w-5 h-5" />
            {isUploading ? 'Uploading...' : 'Upload Image'}
          </button>
          
          <button
            onClick={() => { setShowAiPrompt(true); setShowUrlInput(false); }}
            disabled={isUploading || isGenerating}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50"
          >
            <SparklesIcon className="w-5 h-5" />
            {isGenerating ? 'Generating...' : 'Generate with AI'}
          </button>
          
          <button
            onClick={() => { setShowUrlInput(true); setShowAiPrompt(false); }}
            disabled={isUploading || isGenerating}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <LinkIcon className="w-5 h-5" />
            Paste URL
          </button>
        </div>

        {showUrlInput && (
          <div className="mt-4 flex gap-2 max-w-md mx-auto">
            <input
              type="url"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder="https://example.com/image.jpg"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              autoFocus
            />
            <button
              onClick={handleUrlSubmit}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Add
            </button>
          </div>
        )}

        {showAiPrompt && (
          <div className="mt-4 max-w-lg mx-auto">
            <textarea
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder="Describe the image you want to generate... e.g., 'A professional illustration showing SEO concepts with a search bar and analytics graphs'"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              rows={3}
              autoFocus
            />
            <div className="flex gap-2 mt-2 justify-end">
              <button
                onClick={() => setShowAiPrompt(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAiGenerate}
                disabled={isGenerating || !aiPrompt.trim()}
                className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 flex items-center gap-2"
              >
                {isGenerating ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Generating...
                  </>
                ) : (
                  <>
                    <SparklesIcon className="w-4 h-4" />
                    Generate
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>
    );
  }

  return (
    <div className="image-block">
      <div className="relative group">
        <img
          src={url}
          alt={alt}
          className="w-full rounded-lg"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300"%3E%3Crect fill="%23f3f4f6" width="400" height="300"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="16" x="50%25" y="50%25" text-anchor="middle"%3EImage failed to load%3C/text%3E%3C/svg%3E';
          }}
        />
        
        {/* Overlay controls */}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all rounded-lg flex items-center justify-center">
          <button
            onClick={() => fileInputRef.current?.click()}
            className="opacity-0 group-hover:opacity-100 px-4 py-2 bg-white text-gray-700 rounded-lg shadow-lg hover:bg-gray-50 transition-all"
          >
            Replace Image
          </button>
        </div>
        
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      {/* Alt text and caption inputs */}
      <div className="mt-3 space-y-2">
        <input
          type="text"
          value={alt}
          onChange={(e) => onChange({ alt: e.target.value })}
          placeholder="Alt text (for accessibility)"
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
        <input
          type="text"
          value={caption}
          onChange={(e) => onChange({ caption: e.target.value })}
          placeholder="Caption (optional)"
          className="w-full px-3 py-2 text-sm text-gray-500 border border-gray-200 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent italic"
        />
      </div>
    </div>
  );
};

export default ImageBlock;
